import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import QuickQuote from "../components/QuickQuote";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { cn } from "../lib/utils";

const SERVICE_OPTIONS = [
  "Storm Damage Cleanup",
  "Junk Removal",
  "Estate Clean Out",
  "Light Landscaping",
  "Small Tree Service",
  "Odd Jobs / Manual Labor",
  "Not Sure — Just Need a Hand",
];

const WINDOW_OPTIONS = [
  { v: "morning", l: "Morning (7am–11am)" },
  { v: "midday", l: "Midday (11am–2pm)" },
  { v: "afternoon", l: "Afternoon (2pm–6pm)" },
  { v: "evening", l: "Evening (6pm–8pm)" },
  { v: "flexible", l: "I'm flexible" },
];

export default function BookingPage() {
  const [params] = useSearchParams();
  const canceled = params.get("canceled") === "1";
  const presetService = params.get("service") || "";

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    service: SERVICE_OPTIONS.includes(presetService) ? presetService : "",
    address: "",
    city: "Eau Claire",
    preferred_window: "",
    details: "",
  });
  const [date, setDate] = useState();
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState("consultation");
  const [submitting, setSubmitting] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (canceled) {
      toast.error("Payment canceled. Your booking is saved — you can pay later.");
    }
  }, [canceled]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.full_name.trim()) return "Please share your name.";
    if (!/.+@.+\..+/.test(form.email)) return "A valid email helps us reach you.";
    if (!form.phone.trim()) return "Phone number please.";
    if (!form.service) return "Pick a service (or 'Not Sure').";
    if (!form.address.trim()) return "Job address?";
    if (!form.details.trim()) return "Tell us a little about the job.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
        deposit_package: selectedPkg,
      };
      const { data } = await api.post("/bookings", payload);
      setCreatedBooking(data);

      const pkg = packages.find((p) => p.id === selectedPkg);
      if (pkg && pkg.amount > 0) {
        // Initiate stripe checkout
        const res = await api.post("/checkout/session", {
          booking_id: data.id,
          package_id: selectedPkg,
          origin_url: window.location.origin,
        });
        if (res.data?.url) {
          window.location.href = res.data.url;
          return;
        }
      } else {
        toast.success("Booking received — we'll be in touch within 1 business day.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (createdBooking && (packages.find((p) => p.id === selectedPkg)?.amount || 0) === 0) {
    return <BookingSuccess booking={createdBooking} />;
  }

  return (
    <div className="bg-stone-950 py-16 lg:py-24" data-testid="booking-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        {/* Fast lane on top */}
        <div className="grid lg:grid-cols-5 gap-10 mb-20">
          <div className="lg:col-span-2">
            <div className="label-eyebrow mb-3">Fast Lane</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-stone-50 tracking-tight">
              Just need a <span className="text-yellow-400">call back?</span>
            </h1>
            <p className="mt-5 text-stone-400 leading-relaxed">
              Three fields. Same-day callback with a free estimate. Or use the
              full form below to lock in a date and pay an optional deposit.
            </p>
            <a
              href="tel:7152146563"
              className="mt-6 inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 text-sm font-bold uppercase tracking-[0.2em]"
            >
              Or call (715) 214-6563
            </a>
          </div>
          <div className="lg:col-span-3">
            <QuickQuote />
          </div>
        </div>

        <div className="border-t border-stone-800 pt-16">
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <div className="label-eyebrow mb-3">Schedule a Job</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black text-stone-50 tracking-tight">
              Ready to put it on the <span className="text-yellow-400">calendar?</span>
            </h2>
            <p className="mt-5 text-stone-400 leading-relaxed">
              Full booking — pick a service, a date, and an optional deposit to
              hold your spot. We respond within one business day, usually
              faster.
            </p>
            <div className="mt-8 border border-stone-800 bg-stone-900 p-6 space-y-3 text-sm">
              <div className="flex gap-3 text-stone-200">
                <CheckCircle2 className="h-5 w-5 text-yellow-400 shrink-0" />
                <span>Property maintenance and cleanup throughout Wisconsin.</span>
              </div>
              <div className="flex gap-3 text-stone-200">
                <CheckCircle2 className="h-5 w-5 text-yellow-400 shrink-0" />
                <span>Honest quotes up front — no surprise fees.</span>
              </div>
              <div className="flex gap-3 text-stone-200">
                <CheckCircle2 className="h-5 w-5 text-yellow-400 shrink-0" />
                <span>Eau Claire local; will travel by approved inquiry.</span>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="booking-form"
            className="lg:col-span-3 border border-stone-800 bg-stone-900 p-6 lg:p-10 space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Full name">
                <Input
                  data-testid="booking-input-name"
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  className="bg-stone-950 border-stone-700 focus:border-yellow-500 rounded-none h-11"
                />
              </Field>
              <Field label="Email">
                <Input
                  data-testid="booking-input-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="bg-stone-950 border-stone-700 focus:border-yellow-500 rounded-none h-11"
                />
              </Field>
              <Field label="Phone">
                <Input
                  data-testid="booking-input-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="bg-stone-950 border-stone-700 focus:border-yellow-500 rounded-none h-11"
                />
              </Field>
              <Field label="Service">
                <Select
                  value={form.service}
                  onValueChange={(v) => update("service", v)}
                >
                  <SelectTrigger
                    data-testid="booking-select-service"
                    className="bg-stone-950 border-stone-700 focus:border-yellow-500 rounded-none h-11"
                  >
                    <SelectValue placeholder="What kind of job?" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700 text-stone-100">
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Job address" className="md:col-span-2">
                <Input
                  data-testid="booking-input-address"
                  value={form.address}
                  onChange={(e) => update("address", e.target.value)}
                  placeholder="Street, city, ZIP"
                  className="bg-stone-950 border-stone-700 focus:border-yellow-500 rounded-none h-11"
                />
              </Field>
              <Field label="Preferred date">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="booking-date-trigger"
                      className={cn(
                        "justify-start bg-stone-950 border-stone-700 hover:border-yellow-500 hover:bg-stone-950 rounded-none h-11 text-stone-100 font-normal",
                        !date && "text-stone-500"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-yellow-400" />
                      {date ? format(date, "PPP") : "Pick a day"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-stone-900 border-stone-700">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </Field>
              <Field label="Time window">
                <Select
                  value={form.preferred_window}
                  onValueChange={(v) => update("preferred_window", v)}
                >
                  <SelectTrigger
                    data-testid="booking-select-window"
                    className="bg-stone-950 border-stone-700 focus:border-yellow-500 rounded-none h-11"
                  >
                    <SelectValue placeholder="When works?" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700 text-stone-100">
                    {WINDOW_OPTIONS.map((w) => (
                      <SelectItem key={w.v} value={w.v}>
                        {w.l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Job details">
              <Textarea
                data-testid="booking-input-details"
                rows={5}
                value={form.details}
                onChange={(e) => update("details", e.target.value)}
                placeholder="What's the job? Any access notes, hazards, or photos we should know about?"
                className="bg-stone-950 border-stone-700 focus:border-yellow-500 rounded-none"
              />
            </Field>

            <div>
              <div className="label-eyebrow mb-3">Optional deposit</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {packages.map((p) => {
                  const active = selectedPkg === p.id;
                  return (
                    <button
                      type="button"
                      key={p.id}
                      data-testid={`booking-pkg-${p.id}`}
                      onClick={() => setSelectedPkg(p.id)}
                      className={cn(
                        "text-left p-4 border-2 transition-colors",
                        active
                          ? "border-yellow-500 bg-yellow-500/10"
                          : "border-stone-700 bg-stone-950 hover:border-stone-500"
                      )}
                    >
                      <div className="text-stone-50 font-semibold">{p.label}</div>
                      <div className="text-xs uppercase tracking-[0.18em] text-stone-400 mt-1">
                        {p.amount > 0 ? `$${p.amount.toFixed(2)} via Stripe` : "No charge"}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <Button
              type="submit"
              data-testid="booking-submit-btn"
              disabled={submitting}
              className="w-full bg-yellow-500 text-yellow-950 hover:bg-yellow-400 font-bold uppercase tracking-wider rounded-none h-12 border-2 border-yellow-500 disabled:opacity-60"
            >
              {submitting
                ? "Submitting..."
                : packages.find((p) => p.id === selectedPkg)?.amount > 0
                ? "Submit & Pay Deposit"
                : "Submit Booking Request"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </form>
        </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={cn("space-y-2", className)}>
      <Label className="text-xs uppercase tracking-[0.2em] font-bold text-stone-300">
        {label}
      </Label>
      {children}
    </div>
  );
}

function BookingSuccess({ booking }) {
  return (
    <div className="bg-stone-950 py-32" data-testid="booking-success">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <CheckCircle2 className="h-16 w-16 text-yellow-400 mx-auto" />
        <h1 className="mt-6 font-display text-4xl sm:text-5xl font-black text-stone-50 tracking-tight">
          Request received.
        </h1>
        <p className="mt-5 text-stone-300 text-lg leading-relaxed">
          Thanks {booking.full_name.split(" ")[0]}. We'll reach out within one
          business day to confirm timing and pricing. Confirmation ID:{" "}
          <span className="font-mono text-yellow-400 text-sm">
            {booking.id.slice(0, 8)}
          </span>
        </p>
        <Link
          to="/"
          className="mt-10 inline-flex items-center gap-2 bg-yellow-500 text-yellow-950 font-bold uppercase tracking-wider px-6 py-3 border-2 border-yellow-500 hover:bg-yellow-400"
        >
          Back Home <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
